/**
 * Chauffeur Missions List — Refonte Mobile EPIC 2 (Ticket 3)
 * Separated views: "Reçues" (pending proposals) vs regular mission tabs.
 * Category mismatch warning on acceptance.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import axios from 'axios';
import {
  Calendar, Clock, MapPin, User,
  ChevronLeft, ChevronRight, Loader2, RefreshCw,
  AlertTriangle, Eye, Car, Compass, Check, X, Bell, Navigation
} from 'lucide-react';
import config from 'config';
import { useAuth } from '../../context/AuthContext';
import ShiftControlWidget from './profil/ShiftControlWidget';

const API_URL = config.API_URL;

const STATUS_CFG = {
  assigned:           { label: 'Assignée',          cls: 'bg-blue-500/20 text-blue-400' },
  accepted:           { label: 'Acceptée',          cls: 'bg-purple-500/20 text-purple-400' },
  confirmed:          { label: 'Confirmée',         cls: 'bg-green-500/20 text-green-400' },
  en_route:           { label: 'En route',          cls: 'bg-yellow-500/20 text-yellow-400' },
  on_site:            { label: 'Sur place',         cls: 'bg-orange-500/20 text-orange-400' },
  waiting_for_pickup: { label: 'En attente client', cls: 'bg-orange-500/20 text-orange-400' },
  client_on_board:    { label: 'Client à bord',     cls: 'bg-cyan-500/20 text-cyan-400' },
  dropped_off:        { label: 'Client déposé',     cls: 'bg-indigo-500/20 text-indigo-400' },
  completed:          { label: 'Terminée',          cls: 'bg-green-500/20 text-green-400' },
  cancelled:          { label: 'Annulée',           cls: 'bg-red-500/20 text-red-400' },
};

const PERIOD_LABELS = {
  received:    'Reçues',
  in_progress: 'En cours',
  upcoming:    'À venir',
  today:       "Aujourd'hui",
  past:        'Passées',
};

const TYPE_LABELS = {
  transfer:    { label: 'VTC',       icon: Car,     cls: 'bg-[#C9A24D]/15 text-[#C9A24D]' },
  disposition: { label: 'Mise a dispo', icon: Clock, cls: 'bg-blue-500/15 text-blue-400' },
  excursion:   { label: 'Excursion', icon: Compass,  cls: 'bg-emerald-500/15 text-emerald-400' },
};

const CATEGORY_LABELS = {
  berline: 'Berline Classique',
  beline_vip: 'Berline Premium',
  suv: 'SUV',
  van: 'Van',
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};
const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const ChauffeurMissionsPage = () => {
  const { user } = useAuth();
  const isInterne = user?.fleet_status === 'interne';
  const isTVA = user?.tva_assujetti === true;
  const TVA_RATE = 0.10;

  const formatFee = (fee) => {
    if (!fee) return null;
    const ht = parseFloat(fee);
    const amount = isTVA ? (ht * (1 + TVA_RATE)).toFixed(2) : ht.toFixed(2);
    const label = isTVA ? 'TTC' : 'HT';
    return { amount, label };
  };
  const [searchParams] = useSearchParams();
  const [missions, setMissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(searchParams.get('period') || 'upcoming');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  // Proposal state
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [refusing, setRefusing] = useState(false);
  // Category mismatch confirmation
  const [mismatchConfirm, setMismatchConfirm] = useState(null); // { proposalId, source, vehicle, missionCategory }

  useEffect(() => {
    if (period !== 'received') fetchMissions();
  }, [period, page]);

  // Fetch proposals + vehicles
  const fetchProposalsAndVehicles = useCallback(() => {
    const token = localStorage.getItem('access_token');
    setProposalsLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/dispatch/pro/chauffeur/missions/pending`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/api/dispatch/pro/chauffeur/vehicles`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([pRes, vRes]) => {
      setProposals(pRes.data?.missions || []);
      setVehicles(vRes.data?.vehicles || []);
    }).catch(() => {}).finally(() => setProposalsLoading(false));
  }, []);

  useEffect(() => {
    fetchProposalsAndVehicles();
  }, [fetchProposalsAndVehicles]);

  const attemptAccept = (proposal, vehicle) => {
    const missionCat = proposal.vehicle_category || '';
    const vehicleCat = vehicle.vehicle_category || '';
    if (missionCat && vehicleCat && missionCat !== vehicleCat) {
      setMismatchConfirm({ proposalId: proposal.id, source: proposal.source, vehicle, missionCategory: missionCat });
    } else {
      handleAccept(proposal.id, proposal.source, vehicle.license_plate, false);
    }
  };

  const handleAccept = async (proposalId, source, vehicleId, categoryMismatchAcknowledged) => {
    setAccepting(true);
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(`${API_URL}/api/dispatch/pro/chauffeur/missions/${proposalId}/accept?source=${source}`,
        { vehicle_id: vehicleId, category_mismatch_acknowledged: categoryMismatchAcknowledged },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      setProposals(prev => prev.filter(p => p.id !== proposalId));
      setSelectedProposal(null);
      setMismatchConfirm(null);
      // Switch to "upcoming" tab so the driver sees the accepted mission
      setPeriod('upcoming');
      setPage(1);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    setAccepting(false);
  };

  const handleRefuse = async (proposalId, source) => {
    setRefusing(true);
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(`${API_URL}/api/dispatch/pro/chauffeur/missions/${proposalId}/refuse?source=${source}`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      setProposals(prev => prev.filter(p => p.id !== proposalId));
      setSelectedProposal(null);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    setRefusing(false);
  };

  const fetchMissions = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    try {
      const params = new URLSearchParams({ period, page: page.toString(), per_page: '15' });
      const res = await axios.get(`${API_URL}/api/chauffeur/missions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMissions(res.data.missions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch missions:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-4 max-w-screen-lg mx-auto" data-testid="chauffeur-missions">
      {/* Shift controls for internal drivers */}
      {isInterne && <ShiftControlWidget />}

      {/* Small notification banner if not on Recues tab and proposals exist */}
      {period !== 'received' && proposals.length > 0 && (
        <button onClick={() => setPeriod('received')}
          className="w-full flex items-center gap-2 mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-left"
          data-testid="proposals-banner">
          <Bell className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-xs text-amber-400 font-medium">{proposals.length} nouvelle(s) mission(s) reçue(s)</span>
          <span className="text-[10px] text-amber-400/50 ml-auto">Voir</span>
        </button>
      )}

      {/* Title + refresh */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-white">Mes missions</h2>
          <p className="text-[#E5E5E5]/40 text-xs">
            {period === 'received'
              ? `${proposals.length} mission(s) reçue(s)`
              : `${total} mission(s) ${PERIOD_LABELS[period]?.toLowerCase()}`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { if (period === 'received') fetchProposalsAndVehicles(); else fetchMissions(); }}
          className="text-[#E5E5E5]/50 min-h-[44px] min-w-[44px] touch-manipulation"
          data-testid="refresh-missions"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Period filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5 scrollbar-hide">
        {Object.entries(PERIOD_LABELS).map(([key, label]) => (
          <Button
            key={key}
            variant={period === key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setPeriod(key); setPage(1); }}
            className={`shrink-0 min-h-[44px] px-4 touch-manipulation text-sm relative ${
              period === key
                ? 'bg-[#C9A24D] text-[#0B0B0B] font-semibold'
                : 'border-white/12 text-[#E5E5E5]/70 hover:bg-white/5'
            }`}
            data-testid={`filter-${key}`}
          >
            {label}
            {key === 'received' && proposals.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {proposals.length}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Category mismatch confirmation modal */}
      {mismatchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="category-mismatch-modal">
          <div className="bg-[#1a1a1a] border border-amber-500/30 rounded-2xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-white font-semibold text-sm">Catégorie différente</h3>
            </div>
            <p className="text-white/70 text-xs leading-relaxed">
              La mission demande un véhicule de catégorie <strong className="text-amber-400">{CATEGORY_LABELS[mismatchConfirm.missionCategory] || mismatchConfirm.missionCategory}</strong>, mais vous avez sélectionné un véhicule de catégorie <strong className="text-amber-400">{CATEGORY_LABELS[mismatchConfirm.vehicle.vehicle_category] || mismatchConfirm.vehicle.vehicle_category}</strong>.
            </p>
            <p className="text-white/50 text-[11px] leading-relaxed bg-white/5 rounded-lg p-3">
              En validant, vous acceptez et assumez la tarification correspondant à la catégorie réellement affectée.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setMismatchConfirm(null)}
                className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 text-sm font-medium hover:bg-white/5 transition"
                data-testid="mismatch-cancel"
              >
                Annuler
              </button>
              <button
                onClick={() => handleAccept(mismatchConfirm.proposalId, mismatchConfirm.source, mismatchConfirm.vehicle.license_plate, true)}
                disabled={accepting}
                className="flex-1 py-3 rounded-xl bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition flex items-center justify-center gap-2"
                data-testid="mismatch-confirm"
              >
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" />Confirmer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content: Proposals OR Mission Cards */}
      {period === 'received' ? (
        proposalsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 text-[#C9A24D] animate-spin" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="text-center py-16" data-testid="no-proposals">
            <Bell className="w-10 h-10 text-white/10 mx-auto mb-3" />
            <p className="text-[#E5E5E5]/40 text-sm">Aucune mission reçue</p>
            <p className="text-[#E5E5E5]/20 text-xs mt-1">Les missions proposées par le dispatch apparaîtront ici</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="pending-proposals">
            {proposals.map(p => (
              <ProposalCard
                key={p.id}
                proposal={p}
                vehicles={vehicles}
                isSelected={selectedProposal?.id === p.id}
                onToggle={() => setSelectedProposal(selectedProposal?.id === p.id ? null : p)}
                onAccept={(vehicle) => attemptAccept(p, vehicle)}
                onRefuse={() => handleRefuse(p.id, p.source)}
                accepting={accepting}
                refusing={refusing}
                formatFee={formatFee}
              />
            ))}
          </div>
        )
      ) : (
      /* Regular Mission Cards */
      loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-[#C9A24D] animate-spin" />
        </div>
      ) : missions.length === 0 ? (
        <div className="text-center py-16">
          <Calendar className="w-10 h-10 mx-auto mb-2 text-[#E5E5E5]/15" />
          <p className="text-[#E5E5E5]/40 text-sm">
            Aucune mission {PERIOD_LABELS[period]?.toLowerCase()}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {missions.map((m) => (
            <MissionCard key={m.id} mission={m} />
          ))}
        </div>
      )
      )}

      {/* Pagination */}
      {period !== 'received' && totalPages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <p className="text-[#E5E5E5]/35 text-xs">Page {page}/{totalPages}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-white/12 text-[#E5E5E5] min-h-[44px] min-w-[44px] touch-manipulation"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-white/12 text-[#E5E5E5] min-h-[44px] min-w-[44px] touch-manipulation"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


/* -- Proposal Card -- */
function ProposalCard({ proposal: p, vehicles, isSelected, onToggle, onAccept, onRefuse, accepting, refusing, formatFee }) {
  // Filter only approved vehicles (they already are from backend, but extra safety)
  const approvedVehicles = vehicles.filter(v => v.vehicle_category !== undefined);
  const fee = formatFee ? formatFee(p.proposed_fee) : null;

  return (
    <div className="rounded-xl bg-[#111]/80 border border-amber-500/20 overflow-hidden"
      data-testid={`proposal-${p.id}`}>
      <button className="w-full text-left p-4" onClick={onToggle}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">{p.type}</span>
            <span className="text-[10px] text-white/30">{p.ref_id}</span>
          </div>
          <span className="text-xs text-white/50">{fmtDate(p.start_time)} {fmtTime(p.start_time)}</span>
        </div>
        <div className="flex items-start gap-2 mb-1">
          <MapPin className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-sm text-white/80">{p.pickup_address || '\u2014'}</p>
        </div>
        {p.dropoff_address && (
          <div className="flex items-start gap-2 mb-1 ml-5">
            <MapPin className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
            <p className="text-xs text-white/50">{p.dropoff_address}</p>
          </div>
        )}
        <div className="flex items-center gap-3 mt-2 text-[10px] text-white/30">
          <span className="flex items-center gap-1"><User className="w-3 h-3" />{p.client_name || '\u2014'}</span>
          <span>{p.passenger_count || 0} pax</span>
          {p.vehicle_category && (
            <span className="flex items-center gap-1 text-[#C9A24D]/70">
              <Car className="w-3 h-3" />{CATEGORY_LABELS[p.vehicle_category] || p.vehicle_category}
            </span>
          )}
        </div>
        {/* Proposed fee with HT/TTC */}
        {fee && (
          <div className="flex items-center gap-1.5 mt-2" data-testid="mission-proposed-fee">
            <span className="text-[#C9A24D] font-bold text-base">{fee.amount} €</span>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#C9A24D]/15 text-[#C9A24D]/80">{fee.label}</span>
            <span className="text-white/25 text-[10px]">proposé</span>
          </div>
        )}
      </button>

      {/* Expanded detail + accept/refuse */}
      {isSelected && (
        <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-3">
          {/* Google Maps navigation */}
          {p.pickup_address && (
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.pickup_address)}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/15 text-blue-400 text-xs font-medium hover:bg-blue-500/10 transition min-h-[44px] touch-manipulation"
              data-testid={`navigate-pickup-${p.id}`}>
              <Navigation className="w-4 h-4 shrink-0" />
              <span>Naviguer vers le pickup</span>
            </a>
          )}

          {p.proposal_note && (
            <p className="text-[10px] text-[#C9A24D]/70 italic bg-[#C9A24D]/5 rounded-lg px-3 py-2">Note dispatch : {p.proposal_note}</p>
          )}

          {/* Vehicle selection */}
          {vehicles.length > 0 ? (
            <div className="space-y-2">
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Accepter avec un véhicule :</p>
              {vehicles.map((v, vi) => {
                const hasMismatch = p.vehicle_category && v.vehicle_category && p.vehicle_category !== v.vehicle_category;
                return (
                  <button key={vi} onClick={() => onAccept(v)}
                    disabled={accepting}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition min-h-[56px] touch-manipulation ${
                      hasMismatch
                        ? 'bg-amber-500/5 border-amber-500/20 hover:bg-amber-500/10'
                        : 'bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10'
                    }`}
                    data-testid={`accept-vehicle-${vi}`}>
                    <Car className={`w-5 h-5 shrink-0 ${hasMismatch ? 'text-amber-400' : 'text-emerald-400'}`} />
                    <div className="flex-1 text-left">
                      <p className="text-sm text-white/80 font-medium">{v.brand} {v.model}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-[10px] text-white/40">{v.license_plate} {v.color ? `\u00b7 ${v.color}` : ''}</p>
                        {v.vehicle_category && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/30">{CATEGORY_LABELS[v.vehicle_category] || v.vehicle_category}</span>
                        )}
                      </div>
                      {hasMismatch && (
                        <p className="text-[9px] text-amber-400/70 mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" />Catégorie différente de la mission
                        </p>
                      )}
                    </div>
                    <div className={`flex items-center gap-1 text-xs font-semibold shrink-0 ${hasMismatch ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" />Accepter</>}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-red-400 bg-red-500/5 rounded-lg px-3 py-2">Aucun véhicule approuvé. Contactez le dispatch.</p>
          )}

          <button onClick={onRefuse} disabled={refusing}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/5 border border-red-500/20 hover:bg-red-500/10 text-red-400 text-sm font-medium transition min-h-[48px] touch-manipulation"
            data-testid={`refuse-${p.id}`}>
            {refusing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4" />Refuser cette mission</>}
          </button>
        </div>
      )}
    </div>
  );
}


/* -- Single Mission Card (Ticket 3) -- */
function MissionCard({ mission: m }) {
  const sc = STATUS_CFG[m.status] || STATUS_CFG.assigned;
  const typeCfg = TYPE_LABELS[m.service_type] || TYPE_LABELS.transfer;
  const TypeIcon = typeCfg.icon;

  return (
    <Card
      className="bg-[#161616] border-white/6 overflow-hidden"
      data-testid={`mission-card-${m.id}`}
    >
      <CardContent className="p-0">
        {/* Top bar: time + type badge */}
        <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
          <div className="flex items-center gap-2.5">
            <div className="min-w-[52px] text-center shrink-0">
              <p className="text-[#C9A24D] text-xl font-bold leading-tight" data-testid="mission-time">
                {fmtTime(m.pickup_datetime)}
              </p>
              <p className="text-[#E5E5E5]/30 text-[10px] leading-tight">{fmtDate(m.pickup_datetime)}</p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium ${typeCfg.cls}`} data-testid="mission-type">
              <TypeIcon className="w-3 h-3" />
              {typeCfg.label}
            </span>
          </div>
          <span className={`px-2.5 py-1 rounded-md text-[11px] font-medium ${sc.cls}`} data-testid="mission-status">
            {sc.label}
          </span>
        </div>

        {/* Client + Ref */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2">
            <span className="text-[#C9A24D] font-mono text-[11px]">{m.booking_ref}</span>
            {m.client_name && (
              <span className="text-white/70 text-xs flex items-center gap-1 truncate">
                <User className="w-3 h-3 text-white/30 shrink-0" />
                {m.client_name}
              </span>
            )}
          </div>
        </div>

        {/* Pickup address */}
        <div className="px-4 pb-2.5">
          <div className="flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-[#C9A24D]/60 mt-0.5 shrink-0" />
            <p className="text-[#E5E5E5]/60 text-xs leading-snug truncate">
              {m.pickup_address || m.pickup_city || '\u2014'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-white/[0.02] border-t border-white/5">
          <div className="flex items-center gap-3 text-[10px] text-[#E5E5E5]/35">
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" /> {m.passengers} pax
            </span>
            <span className="capitalize">{m.vehicle_category}</span>
            {m.has_special_requests && (
              <span className="text-yellow-400 flex items-center gap-0.5">
                <AlertTriangle className="w-3 h-3" />
              </span>
            )}
          </div>
          <Link
            to={`/chauffeur/missions/${m.id}`}
            className="inline-flex items-center gap-1 text-[#C9A24D] text-xs font-medium min-h-[44px] touch-manipulation hover:underline"
            data-testid={`voir-detail-${m.id}`}
          >
            <Eye className="w-3.5 h-3.5" />
            Voir détail
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default ChauffeurMissionsPage;
